import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import api from "../../utils/api";
import OrdersPreview from "../OrdersPreview";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import MemberSidebar, { SIDEBAR_W as MEMBER_SIDEBAR_W } from "./MemberSidebar";
import MemberTopBar from "./MemberTopBar";
import { PermissionsContext } from "../../utils/PermissionsContext";
import { ModulesContext } from "../../utils/ModulesContext";
import IntroLoader from "../../IntroLoader/IntroLoader";

const Home = () => {
  const [userFullName, setUserFullName] = useState("User");
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [modules, setModules] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.setItem("lastChecked", new Date().toISOString());
  };
  const [openPropertiesMenu, setOpenPropertiesMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [memberAccounts, setMemberAccounts] = useState([]);

  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const location = useLocation();

  const isMember = Number(localStorage.getItem("roleId")) === 3;
  const sidebarWidth = isMobile ? 0 : isMember ? MEMBER_SIDEBAR_W : sidebarCollapsed ? 80 : 250;

  useEffect(() => {
    const init = async () => {
      if (!localStorage.getItem("authToken")) {
        navigate("/login");
        return;
      }

      const roleId = localStorage.getItem("roleId");
      const firstName = localStorage.getItem("firstName");
      const lastName = localStorage.getItem("lastName");
      let storedRoles = [];
      try {
        storedRoles = JSON.parse(localStorage.getItem("roles") || "[]");
      } catch {
        storedRoles = [];
      }
      const username = localStorage.getItem("username");

      if (!username) {
        setLoadingPermissions(false);
        return;
      }

      setUserFullName(`${firstName || ""} ${lastName || ""}`.trim() || "User");
      setRoles(storedRoles);

      // For member users, fetch their associated accounts for the sidebar switcher
      if (Number(roleId) === 3 && username) {
        try {
          const userRes = await api.get(`/users/${username}`);
          const accounts = (userRes.data?.AssociatedAccounts || []).map(String);
          setMemberAccounts(accounts);
        } catch (err) {
          console.error("Failed to fetch member accounts", err);
        }
      }

      try {
        const response = await api.get("/auth/permissions", {
          headers: { "x-roleid": roleId },
        });
        const perms = response.data.permissions || [];
        // If system admin, store that flag and ensure all system prefs are available
        if (response.data.isSystemAdmin) {
          localStorage.setItem("isSystemAdmin", "1");
        } else {
          localStorage.removeItem("isSystemAdmin");
        }
        setPermissions(perms);
      } catch (err) {
        console.error("Error fetching permissions", err);
      } finally {
        setLoadingPermissions(false);
      }
    };

    init();
  }, [navigate, location.pathname]);

  const fetchModules = useCallback(async () => {
    try {
      const res = await api.get("/modules");
      setModules(res.data || []);
    } catch {
      // Non-fatal. sidebar will default to showing all items
    }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Default to 1 hour ago so the first poll surfaces recent notifications
        const fallback = new Date(Date.now() - 60 * 60 * 1000).toISOString();
        const lastChecked = new Date(
          localStorage.getItem("lastChecked") || fallback
        );

        const response = await api.get("/getNotifications?hours=1");
        const notificationsResponse = response.data.notifications || [];

        const newNotifications = notificationsResponse.filter(
          (n) => new Date(n.date) > lastChecked
        );

        if (newNotifications.length > 0) {
          setNotifications((prev) => [...prev, ...newNotifications]);
          setUnreadCount((prev) => prev + newNotifications.length);

          const latestTimestamp = newNotifications.reduce(
            (latest, current) =>
              new Date(current.date) > new Date(latest)
                ? current.date
                : latest,
            lastChecked
          );

          localStorage.setItem(
            "lastChecked",
            new Date(latestTimestamp).toISOString()
          );
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const hasPermission = (perm) => permissions.includes(perm);
  const isActive = (path) => location.pathname === path;

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleSignOut = () => {
    localStorage.clear();
    navigate("/login");
  };

  const openSettingsMenu = (event) => setSettingsAnchorEl(event.currentTarget);
  const closeSettingsMenu = () => setSettingsAnchorEl(null);

  const handleSystemPreferences = () => {
    navigate("/home/system-preferences");
    closeSettingsMenu();
  };

  const handleUserProfile = () => {
    navigate("/home/user-profile");
    closeSettingsMenu();
  };

  const handleThemeSettings = () => {
    navigate("/home/theme-settings");
    closeSettingsMenu();
  };

  const openNotificationsMenu = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    setUnreadCount(0);
    localStorage.setItem("lastChecked", new Date().toISOString());
  };

  const closeNotificationsMenu = () => setNotificationsAnchorEl(null);

  const pageTitles = {
    "/home": "Dashboard",
    "/home/users": "Users",
    "/home/coverletters": "Cover Letters",
    "/home/properties": "Properties",
    "/home/architecturalrequest": "Architectural Request",
    "/home/workorders": "Work Orders",
    "/home/members": "Members",
    "/home/filemanager": "File Manager",
    "/home/violations": "Violations",
    "/home/reports": "Reports",
    "/home/integrations": "Integrations",
    "/home/Voting": "E-Votign",
  };

  const pageTitle = pageTitles[location.pathname] || "MY HOA";

  if (loadingPermissions) {
    return <IntroLoader message="Loading your dashboard..." />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minHeight: "100vh",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <OrdersPreview />

      {isMember ? (
        <MemberSidebar
          userFullName={userFullName}
          handleSignOut={handleSignOut}
          accounts={memberAccounts}
        />
      ) : (
        <Sidebar
          userFullName={userFullName}
          openPropertiesMenu={openPropertiesMenu}
          setOpenPropertiesMenu={setOpenPropertiesMenu}
          hasPermission={hasPermission}
          isActive={isActive}
          handleSignOut={handleSignOut}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          modules={modules}
        />
      )}

      <Box
        sx={{
          flexGrow: 1,
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          transition: "margin-left 0.3s ease",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: isMobile ? "100%" : `calc(100% - ${sidebarWidth}px)`,
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        {isMember ? (
          <MemberTopBar
            userFullName={userFullName}
            hasPermission={hasPermission}
            notifications={notifications}
            notificationsAnchorEl={notificationsAnchorEl}
            openNotificationsMenu={openNotificationsMenu}
            closeNotificationsMenu={closeNotificationsMenu}
            unreadCount={unreadCount}
            navigate={navigate}
            handleUserProfile={handleUserProfile}
            handleSignOut={handleSignOut}
          />
        ) : (
          <TopBar
            pageTitle={pageTitle}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            hasPermission={hasPermission}
            notifications={notifications}
            notificationsAnchorEl={notificationsAnchorEl}
            openNotificationsMenu={openNotificationsMenu}
            closeNotificationsMenu={closeNotificationsMenu}
            clearNotifications={clearNotifications}
            unreadCount={unreadCount}
            navigate={navigate}
            settingsAnchorEl={settingsAnchorEl}
            openSettingsMenu={openSettingsMenu}
            closeSettingsMenu={closeSettingsMenu}
            handleUserProfile={handleUserProfile}
            handleSystemPreferences={handleSystemPreferences}
            handleThemeSettings={handleThemeSettings}
          />
        )}

        <ModulesContext.Provider value={{ modules, refetch: fetchModules }}>
          <PermissionsContext.Provider value={permissions}>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                overflowX: "hidden",
                p: isMobile ? 1.5 : 2,
                pb: isMobile ? 3 : 2,
                width: "100%",
                boxSizing: "border-box",
                bgcolor: "background.default",
                transition: "background-color 0.3s ease",
              }}
            >
              <Outlet />
            </Box>
          </PermissionsContext.Provider>
        </ModulesContext.Provider>
      </Box>
    </Box>
  );
};

export default Home;